import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote, Star } from "lucide-react";
import { SITE } from "../../../lib/siteData";
import { fadeUp } from "../../../lib/motion";

function Stars({ rating = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={16}
          className={i < rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ t, i }) {
  return (
    <figure
      className="group h-full flex flex-col rounded-[28px] bg-white border border-slate-200/70 shadow-[0_10px_30px_-15px_rgba(1,18,51,0.25)] p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-20px_rgba(1,18,51,0.35)]"
      data-testid={`testimonial-${i}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src={t.photo}
            alt={t.name}
            loading="lazy"
            className="w-14 h-14 rounded-full object-cover ring-2 ring-[#011233]/10"
          />
          <div>
            <div className="text-[15px] font-semibold text-[#011233] leading-tight">
              {t.name}
            </div>
            <div className="text-[12px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
              {t.region}
            </div>
          </div>
        </div>
        <Quote size={28} className="text-[#011233]/15" />
      </div>

      <div className="mt-5">
        <Stars rating={t.rating || 5} />
      </div>

      <blockquote className="mt-4 text-[15.5px] text-slate-700 leading-relaxed flex-1">
        &ldquo;{t.quote}&rdquo;
      </blockquote>
    </figure>
  );
}

export default function TestimonialsSection() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    slidesToScroll: 1,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    // Auto-advance every 6s
    const id = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 6000);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (idx) => emblaApi && emblaApi.scrollTo(idx);

  return (
    <section
      id="testimonials"
      className="py-24 sm:py-32 bg-gradient-to-b from-white to-slate-50/60 overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div {...fadeUp} className="text-center mb-14 sm:mb-16">
          <p className="text-[12px] uppercase tracking-[0.36em] text-[#011233] font-semibold">
            Client stories
          </p>
          <h2 className="mt-5 font-heading font-bold text-3xl sm:text-5xl text-[#011233] tracking-tight">
            Trusted by real families.
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto text-[15px] sm:text-base">
            Independent means we shop for you. Here&apos;s what our neighbors say.
          </p>
        </motion.div>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
            <div className="flex -mx-3">
              {SITE.testimonials.map((t, i) => (
                <div
                  key={`${t.name}-${i}`}
                  className="px-3 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <TestimonialCard t={t} i={i} />
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="mt-10 flex items-center justify-between gap-6">
            <div className="flex items-center gap-2" data-testid="testimonials-dots">
              {snaps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => scrollTo(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  data-testid={`testimonial-dot-${idx}`}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === selected ? "w-8 bg-[#011233]" : "w-1.5 bg-slate-300 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={scrollPrev}
                disabled={!canPrev}
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
                className="w-11 h-11 rounded-full border border-slate-300 text-[#011233] flex items-center justify-center hover:bg-[#011233] hover:text-white hover:border-[#011233] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                onClick={scrollNext}
                disabled={!canNext}
                aria-label="Next testimonial"
                data-testid="testimonial-next"
                className="w-11 h-11 rounded-full border border-slate-300 text-[#011233] flex items-center justify-center hover:bg-[#011233] hover:text-white hover:border-[#011233] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
